
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  deep:   '#0d0b14',
  card:   '#15121e',
  raised: '#1a1626',
  violet: '#5a3d99',
  accent: '#7c5cbf',
  lilac:  '#c9a0e8',
  ivory:  '#f2ece0',
  muted:  'rgba(242,236,224,0.6)',
  faint:  'rgba(242,236,224,0.06)',
  border: 'rgba(201,160,232,0.12)',
  gold:   '#e2b96a',
};
const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};
const BG = 'linear-gradient(160deg, #0d0b14 0%, #110d1a 40%, #160e20 70%, #0d0b14 100%)';

const MOODS = [
  { emoji: '🌙', label: 'Longing' },
  { emoji: '⚡', label: 'Electric' },
  { emoji: '🌸', label: 'Tender' },
  { emoji: '🥀', label: 'Forbidden' },
  { emoji: '🕯️', label: 'Nostalgic' },
  { emoji: '🌊', label: 'Wild' },
];

const STORY_CARDS = [
  {
    mood: 'Electric',
    setting: 'Strangers in a Hotel',
    narrator: 'Aria',
    excerpt: 'The elevator doors close. Your eyes meet in the mirror — and neither of you looks away. His hand moves to the stop button, slowly, like a question…',
  },
  {
    mood: 'Forbidden',
    setting: 'After Hours at the Office',
    narrator: 'Thomas',
    excerpt: 'Everyone else has gone home. The building is yours. She says your name once — just once — and something between you that was always almost breaks…',
  },
  {
    mood: 'Longing',
    setting: 'Paris, 2am',
    narrator: 'Charlotte',
    excerpt: 'Rain on the window. The city asleep. You have been here before — this exact moment — but never with someone who makes the silence feel like this…',
  },
];

export default function Landing({ user }) {
  const navigate  = useNavigate();
  const logoRef   = useRef(null);

  useEffect(() => {
    let frame, t = 0;
    const animate = () => {
      t += 0.006;
      if (logoRef.current) {
        const g = 40 + Math.sin(t) * 16;
        const g2 = g * 0.45;
        logoRef.current.style.filter =
          `drop-shadow(0 0 ${g}px rgba(90,61,153,0.7)) drop-shadow(0 0 ${g2}px rgba(201,160,232,0.3)) drop-shadow(0 0 ${g*1.4}px rgba(90,61,153,0.2))`;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleCTA = () => navigate(user ? '/studio' : '/taster');

  return (
    <div style={{ minHeight:'100vh', background:BG, color:C.ivory,
      fontFamily:font.ui, fontWeight:300, display:'flex',
      flexDirection:'column', alignItems:'center',
      padding:'0 0 80px', position:'relative', overflow:'hidden', boxSizing:'border-box', width:'100%' }}>

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        .story-card:hover { transform: translateY(-3px) !important; border-color: rgba(201,160,232,0.28) !important; }
        .mood-pill:hover { background: rgba(201,160,232,0.12) !important; color: rgba(242,236,224,0.9) !important; }
        .cta-btn:hover { transform: scale(1.02); box-shadow: 0 8px 40px rgba(90,61,153,0.6), 0 2px 8px rgba(0,0,0,0.3) !important; }
      `}</style>

      <div style={{ position:'absolute', width:'600px', height:'600px', borderRadius:'50%',
        background:'radial-gradient(circle, rgba(90,61,153,0.2) 0%, transparent 70%)',
        top:'-160px', left:'50%', transform:'translateX(-50%)', pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:'350px', height:'350px', borderRadius:'50%',
        background:'radial-gradient(circle, rgba(201,160,232,0.07) 0%, transparent 70%)',
        top:'45%', left:'-100px', pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:'350px', height:'350px', borderRadius:'50%',
        background:'radial-gradient(circle, rgba(226,185,106,0.06) 0%, transparent 70%)',
        top:'35%', right:'-100px', pointerEvents:'none' }} />

      <div style={{ width:'100%', maxWidth:'min(480px, 100vw)', display:'flex',
        justifyContent:'flex-end', alignItems:'center',
        padding:'24px clamp(16px, 5vw, 28px)', position:'relative', zIndex:1 }}>
        <button onClick={() => navigate(user ? '/studio' : '/auth')} style={{
          background:'transparent', border:`1px solid ${C.border}`,
          color:C.muted, fontFamily:font.ui, fontWeight:300,
          fontSize:'12px', padding:'7px 16px', borderRadius:'20px',
          cursor:'pointer', letterSpacing:'0.06em' }}>
          {user ? 'MY STORIES' : 'SIGN IN'}
        </button>
      </div>

      <div style={{ marginTop:'0px', marginBottom:'0px', position:'relative', zIndex:1,
        animation:'fadeUp 0.9s ease both' }}>
        <img ref={logoRef} src="/logo.png" alt="YourSiren"
          style={{ width:'320px', height:'auto',
            filter:'drop-shadow(0 0 40px rgba(90,61,153,0.7))' }} />
      </div>

      <div style={{ textAlign:'center', padding:'0 clamp(16px, 5vw, 32px)', maxWidth:'420px',
        position:'relative', zIndex:1, marginBottom:'12px',
        animation:'fadeUp 1s ease 0.15s both' }}>
        <h1 style={{ fontFamily:font.display, fontStyle:'italic', fontSize:'32px',
          fontWeight:400, lineHeight:1.25, color:C.ivory, margin:'0 0 14px',
          textShadow:'0 2px 24px rgba(90,61,153,0.35)' }}>
          Your fantasy, your words —
          <span style={{ color:C.lilac }}> whispered back to you</span>
        </h1>
        <p style={{ fontFamily:font.ui, fontWeight:300, fontSize:'14px',
          color:C.muted, lineHeight:1.8, margin:'0 0 6px' }}>
          You design the scene. Name the characters. Set the mood.
          We write a bespoke story built around your exact fantasy —
          then a voice of your choosing reads it back to you, intimately.
        </p>
        <p style={{ fontFamily:font.body, fontStyle:'italic', fontSize:'13px',
          color:C.lilac, opacity:0.8, letterSpacing:'0.04em', margin:'8px 0 0' }}>
          Every story is unique. Every word written for you alone.
        </p>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:'6px',
        marginBottom:'28px', animation:'fadeUp 1s ease 0.25s both',
        position:'relative', zIndex:1 }}>
        <span style={{ color:C.gold, fontSize:'12px', letterSpacing:'0.04em' }}>★★★★★</span>

      </div>

      <div style={{ width:'100%', maxWidth:'min(400px, 100vw)', padding:'0 clamp(16px, 5vw, 28px)',
        position:'relative', zIndex:1, marginBottom:'44px',
        animation:'fadeUp 1s ease 0.3s both' }}>
        <button className="cta-btn" onClick={handleCTA} style={{
          width:'100%', padding:'18px',
          background:`linear-gradient(135deg, ${C.violet} 0%, ${C.accent} 60%, #9b6fd4 100%)`,
          border:'none', borderRadius:'14px', color:C.ivory,
          fontFamily:font.ui, fontWeight:300, fontSize:'16px',
          letterSpacing:'0.08em', cursor:'pointer', transition:'all 0.2s',
          boxShadow:'0 6px 32px rgba(90,61,153,0.45), 0 2px 8px rgba(0,0,0,0.3)' }}>
          Begin my story ✦
        </button>
        <p style={{ textAlign:'center', fontSize:'11px', color:'rgba(242,236,224,0.3)',
          fontFamily:font.ui, fontWeight:300, margin:'12px 0 0', letterSpacing:'0.04em' }}>
          No account needed · Free taster · from $9.99/mo
        </p>
      </div>

      <div style={{ width:'100%', maxWidth:'min(480px, 100vw)', position:'relative', zIndex:1,
        marginBottom:'44px', animation:'fadeUp 1s ease 0.35s both' }}>
        <div style={{ fontSize:'10px', fontFamily:font.ui, fontWeight:300,
          letterSpacing:'0.2em', textTransform:'uppercase', color:C.lilac,
          marginBottom:'14px', textAlign:'center', opacity:0.6 }}>
          Choose your mood
        </div>
        <div style={{ display:'flex', gap:'8px', overflowX:'auto', padding:'4px clamp(16px, 5vw, 28px) 8px',
          scrollbarWidth:'none' }}>
          {MOODS.map(m => (
            <button key={m.label} className="mood-pill" onClick={handleCTA} style={{
              flexShrink:0, padding:'10px 16px', borderRadius:'24px',
              background:'rgba(201,160,232,0.06)', border:`1px solid ${C.border}`,
              color:C.muted, fontFamily:font.ui, fontWeight:300, fontSize:'13px',
              cursor:'pointer', transition:'all 0.15s', whiteSpace:'nowrap',
              display:'flex', alignItems:'center', gap:'7px' }}>
              <span>{m.emoji}</span><span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ width:'100%', maxWidth:'min(400px, 100vw)', padding:'0 clamp(16px, 5vw, 28px)',
        position:'relative', zIndex:1, marginBottom:'44px' }}>
        <div style={{ fontSize:'10px', fontFamily:font.ui, fontWeight:300,
          letterSpacing:'0.22em', textTransform:'uppercase',
          color:C.lilac, marginBottom:'20px', textAlign:'center', opacity:0.6 }}>
          How it works
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {[
            ['01', 'You design the fantasy', 'Set the mood, scene, characters — add names, genders, intensity. It is entirely your creation.'],
            ['02', 'We write it, just for you', 'AI crafts a bespoke literary story around your exact choices. No template. No repetition. Yours alone.'],
            ['03', 'A voice reads it back to you', 'Pick your narrator. Press play. Your fantasy, whispered to you in an intimate voice for 8 to 9 minutes.'],
          ].map(([n, title, body]) => (
            <div key={n} style={{ display:'flex', gap:'16px',
              background:'rgba(21,18,30,0.8)', border:`1px solid ${C.border}`,
              borderRadius:'14px', padding:'18px 20px' }}>
              <div style={{ fontFamily:font.display, fontStyle:'italic',
                fontSize:'24px', color:C.violet, lineHeight:1, minWidth:'30px' }}>{n}</div>
              <div>
                <div style={{ fontFamily:font.body, fontStyle:'italic',
                  fontSize:'15px', color:C.ivory, marginBottom:'5px' }}>{title}</div>
                <div style={{ fontFamily:font.ui, fontWeight:300,
                  fontSize:'13px', color:C.muted, lineHeight:1.65 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width:'100%', maxWidth:'min(480px, 100vw)', padding:'0 clamp(16px, 5vw, 28px)',
        position:'relative', zIndex:1, marginBottom:'44px',
        animation:'fadeUp 1s ease 0.4s both' }}>
        <div style={{ fontSize:'10px', fontFamily:font.ui, fontWeight:300,
          letterSpacing:'0.2em', textTransform:'uppercase', color:C.lilac,
          marginBottom:'16px', textAlign:'center', opacity:0.6 }}>
          Stories written for real listeners
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {STORY_CARDS.map((card, i) => (
            <div key={i} className="story-card" onClick={handleCTA} style={{
              background:'rgba(21,18,30,0.9)', border:`1px solid ${C.border}`,
              borderRadius:'16px', padding:'18px 20px', cursor:'pointer',
              transition:'all 0.2s', position:'relative', overflow:'hidden', boxSizing:'border-box', width:'100%' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                <span style={{ fontSize:'11px', fontFamily:font.ui, fontWeight:300,
                  color:C.lilac, background:'rgba(201,160,232,0.1)',
                  border:`1px solid ${C.border}`, borderRadius:'12px',
                  padding:'3px 10px', letterSpacing:'0.04em' }}>{card.mood}</span>
                <span style={{ fontSize:'11px', color:'rgba(242,236,224,0.35)', fontFamily:font.ui }}>·</span>
                <span style={{ fontSize:'11px', color:'rgba(242,236,224,0.45)',
                  fontFamily:font.ui, fontWeight:300 }}>{card.setting}</span>
                <span style={{ marginLeft:'auto', fontSize:'11px',
                  color:'rgba(242,236,224,0.3)', fontFamily:font.ui }}>♪ {card.narrator}</span>
              </div>
              <p style={{ fontFamily:font.body, fontStyle:'italic', fontSize:'14px',
                color:'rgba(242,236,224,0.7)', lineHeight:1.7, margin:0, position:'relative' }}>
                "{card.excerpt}"
                <span style={{ position:'absolute', bottom:0, right:0, width:'55%', height:'100%',
                  background:'linear-gradient(to right, transparent, rgba(21,18,30,0.97))',
                  pointerEvents:'none' }} />
              </p>
              <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'12px' }}>
                <div style={{ width:'24px', height:'24px', borderRadius:'50%',
                  background:`linear-gradient(135deg, ${C.violet}, ${C.accent})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'9px', color:C.ivory, flexShrink:0 }}>▶</div>
                <span style={{ fontSize:'11px', color:C.lilac, opacity:0.6,
                  fontFamily:font.ui, letterSpacing:'0.06em' }}>Write yours like this →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width:'100%', maxWidth:'min(400px, 100vw)', padding:'0 clamp(16px, 5vw, 28px)',
        position:'relative', zIndex:1, marginBottom:'44px' }}>
        <div style={{ background:'rgba(21,18,30,0.6)', border:`1px solid ${C.border}`,
          borderLeft:`3px solid ${C.violet}`, borderRadius:'12px', padding:'22px 24px' }}>
          <p style={{ fontFamily:font.body, fontStyle:'italic', fontSize:'17px',
            color:C.muted, margin:'0 0 12px', lineHeight:1.65 }}>
            "I have never felt so seen by a story. It knew exactly what I wanted — because I told it. Then it handed that back to me in a voice I could lose myself in."
          </p>
          <div style={{ fontSize:'11px', color:'rgba(242,236,224,0.3)',
            fontFamily:font.ui, letterSpacing:'0.08em' }}>— Listener, Desire plan</div>
        </div>
      </div>

      <div style={{ width:'100%', maxWidth:'min(400px, 100vw)', padding:'0 clamp(16px, 5vw, 28px)',
        position:'relative', zIndex:1, textAlign:'center' }}>
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:'36px' }}>
          <p style={{ fontFamily:font.display, fontStyle:'italic',
            fontSize:'22px', color:C.ivory, marginBottom:'8px', lineHeight:1.4 }}>
            Your story is waiting to be written.
          </p>
          <p style={{ fontFamily:font.ui, fontWeight:300, fontSize:'13px',
            color:C.muted, marginBottom:'24px', lineHeight:1.7 }}>
            No two stories are ever the same. This one is yours.
          </p>
          <button className="cta-btn" onClick={handleCTA} style={{
            padding:'16px 44px',
            background:`linear-gradient(135deg, ${C.violet}, ${C.accent})`,
            border:'none', borderRadius:'12px', color:C.ivory,
            fontFamily:font.ui, fontWeight:300, fontSize:'15px',
            letterSpacing:'0.08em', cursor:'pointer', transition:'all 0.2s',
            boxShadow:'0 4px 24px rgba(90,61,153,0.4)' }}>
            Begin my story ✦
          </button>
          <p style={{ fontSize:'11px', color:'rgba(242,236,224,0.25)',
            fontFamily:font.ui, marginTop:'14px' }}>Cancel anytime. No commitment.</p>
        </div>
      </div>

    </div>
  );
}
